import { Controller } from '@hotwired/stimulus';
import { type ModalController } from './modal';
import {csrfHeaders} from '../services/form.services';

type ProfileUpdateResponse = {
    success: boolean;
    detail?: string;
    unit_system?: string;
    goal?: string;
    show_calories_pref?: boolean;
    show_exercise_pref?: boolean;
    calorie_lock_enabled?: boolean;
    goal_settings_html?: string;
    profile_summary_html?: string;
    targets_panel_html?: string;
};

/**
 * UserProfileController handles the user profile/goals page interactions.
 *
 * Features:
 * - Unit system toggle (metric/imperial)
 * - Dynamic unit label updates
 * - Info modal management
 */
export class UserProfileController extends Controller {
    private static readonly conversionDisplayPrecision = 2;

    static outlets = ["modal"]
    declare readonly modalOutlets: ModalController[]

    static targets = [
        'profileForm',
        'saveStatus',
        'unitSystemInput',
        'unitToggle',
        'toggleIndicator',
        'weightUnit',
        'heightUnit',
        'waterUnit',
        'sexInfoModal',
        'ageInput',
        'goalSelect',
        'youthGoalWarning',
        'caloriesToggleWrapper',
        'caloriesToggle',
        'caloriesToggleIndicator',
        'caloriesPreferenceInput',
        'exerciseToggle',
        'exerciseToggleIndicator',
        'exercisePreferenceInput',
        'calorieLockControls',
        'calorieLockEnabledInput',
        'calorieLockActionInput',
        'calorieLockPinInput',
        'lockModal',
        'unlockModal',
        'lockConfirmStep',
        'lockPinStep',
        'lockPinError',
        'unlockPinError',
        'unlockModalTitle',
        'unlockModalDescription',
        'unlockSubmitLabel',
        'goalSettingsPanel',
        'profileSummaryPanel',
        'targetsPanel'
    ];

    declare readonly profileFormTarget: HTMLFormElement;
    declare readonly saveStatusTarget: HTMLElement;
    declare readonly unitSystemInputTarget: HTMLInputElement;
    declare readonly unitToggleTarget: HTMLButtonElement;
    declare readonly toggleIndicatorTarget: HTMLElement;
    declare readonly weightUnitTarget: HTMLElement;
    declare readonly heightUnitTarget: HTMLElement;
    declare readonly waterUnitTarget: HTMLElement;
    declare readonly sexInfoModalTarget: HTMLElement;
    declare readonly ageInputTarget: HTMLInputElement;
    declare readonly goalSelectTarget: HTMLSelectElement;
    declare readonly youthGoalWarningTarget: HTMLElement;
    declare readonly caloriesToggleWrapperTarget: HTMLElement;
    declare readonly caloriesToggleTarget: HTMLButtonElement;
    declare readonly caloriesToggleIndicatorTarget: HTMLElement;
    declare readonly caloriesPreferenceInputTarget: HTMLInputElement;
    declare readonly exerciseToggleTarget: HTMLButtonElement;
    declare readonly exerciseToggleIndicatorTarget: HTMLElement;
    declare readonly exercisePreferenceInputTarget: HTMLInputElement;
    declare readonly hasExerciseToggleTarget: boolean;
    declare readonly hasExerciseToggleIndicatorTarget: boolean;
    declare readonly hasExercisePreferenceInputTarget: boolean;
    declare readonly calorieLockControlsTarget: HTMLElement;
    declare readonly calorieLockEnabledInputTarget: HTMLInputElement;
    declare readonly calorieLockActionInputTarget: HTMLInputElement;
    declare readonly calorieLockPinInputTarget: HTMLInputElement;
    declare readonly lockModalTarget: HTMLElement;
    declare readonly unlockModalTarget: HTMLElement;
    declare readonly lockConfirmStepTarget: HTMLElement;
    declare readonly lockPinStepTarget: HTMLElement;
    declare readonly lockPinErrorTarget: HTMLElement;
    declare readonly unlockPinErrorTarget: HTMLElement;
    declare readonly unlockModalTitleTarget: HTMLElement;
    declare readonly unlockModalDescriptionTarget: HTMLElement;
    declare readonly unlockSubmitLabelTarget: HTMLElement;
    declare readonly goalSettingsPanelTarget: HTMLElement;
    declare readonly profileSummaryPanelTarget: HTMLElement;
    declare readonly targetsPanelTarget: HTMLElement;
    private pendingCaloriesPreferenceChange: boolean | null = null;
    private savingUnitSystem = false;
    private savingCaloriesPreference = false;
    private savingExercisePreference = false;
    private savingGoalPreference = false;
    private savingProfile = false;
    private pendingProfileAutosave = false;
    private lastSavedGoal = '';

    /**
     * Initialize the controller and set up the unit toggle based on saved value
     */
    connect(): void {
        this.lastSavedGoal = this.goalSelectTarget.value;
        this.syncToggleWithSavedValue();
        this.syncYouthGoalWarning();
        this.syncCaloriesToggleWithSavedValue();
        if (this.hasExercisePreferenceControls()) {
            this.syncExerciseToggleWithSavedValue();
        }
        this.syncCaloriesToggleVisibility();
        this.syncCalorieLockVisibility();
        this.syncCaloriesLockState();
        this.setSaveStatus('Changes save automatically.');
    }

    async autosaveProfile(): Promise<void> {
        this.syncYouthGoalWarning();

        if (this.savingGoalPreference || this.savingUnitSystem || this.savingCaloriesPreference) {
            this.pendingProfileAutosave = true;
            return;
        }

        if (!this.profileFormTarget.checkValidity()) {
            this.setSaveStatus('Complete all required fields to save.');
            return;
        }

        if (this.savingProfile) {
            this.pendingProfileAutosave = true;
            return;
        }

        this.savingProfile = true;
        this.setSaveStatus('Saving changes…');

        try {
            const payload = await this.persistProfileFields();
            if (!payload?.success) {
                this.setSaveStatus(payload?.detail ?? 'Could not save changes.', true);
                return;
            }

            this.applyProfileUpdate(payload);
            this.setSaveStatus('Changes saved.');
        } finally {
            this.savingProfile = false;
            if (this.pendingProfileAutosave) {
                this.pendingProfileAutosave = false;
                void this.autosaveProfile();
            }
        }
    }

    /**
     * Toggle between metric and imperial unit systems
     */
    async toggleUnitSystem(): Promise<void> {
        if (this.savingUnitSystem || this.savingGoalPreference) {
            return;
        }

        const isImperial = this.unitToggleTarget.getAttribute('aria-checked') === 'true';
        const targetUnitSystem = isImperial ? 'metric' : 'imperial';

        if (isImperial) {
            // Switch to metric
            this.setMetricUnits(true);
        } else {
            // Switch to imperial
            this.setImperialUnits(true);
        }
        this.savingUnitSystem = true;
        this.setSaveStatus('Saving changes…');
        const saved = await this.saveUnitSystemPreference(targetUnitSystem);
        this.savingUnitSystem = false;
        if (saved?.success) {
            this.applyProfileUpdate(saved);
            this.setSaveStatus('Changes saved.');
            return;
        }
        if (!saved?.success) {
            if (targetUnitSystem === 'imperial') {
                this.setMetricUnits(true);
            } else {
                this.setImperialUnits(true);
            }
            this.setSaveStatus(saved?.detail ?? 'Could not save changes.', true);
        }
    }

    /**
     * Show the biological sex information modal
     */
    showSexInfoModal(): void {
        this.sexInfoModalTarget.classList.remove('hidden');
    }

    /**
     * Hide the biological sex information modal
     */
    hideSexInfoModal(): void {
        this.sexInfoModalTarget.classList.add('hidden');
    }

    /**
     * Update warning if a user is under 16 and selects weight loss.
     */
    syncYouthGoalWarning(): void {
        const age = Number(this.ageInputTarget.value);
        const goal = this.goalSelectTarget.value;
        const shouldShow = Number.isFinite(age) && age > 0 && age < 16 && goal === 'fat_loss';

        if (shouldShow) {
            this.youthGoalWarningTarget.classList.remove('hidden');
        } else {
            this.youthGoalWarningTarget.classList.add('hidden');
        }
    }

    async toggleCaloriesPreference(): Promise<void> {
        if (this.savingCaloriesPreference || this.savingGoalPreference) {
            return;
        }

        if (this.isCaloriesLocked()) {
            this.showUnlockModal(!this.isCaloriesEnabled());
            return;
        }
        const previous = this.isCaloriesEnabled();
        const previousGoal = this.goalSelectTarget.value;
        const goalWasDisabled = this.goalSelectTarget.disabled;
        this.setCaloriesPreference(!previous);
        if (previous) {
            this.previewTrackMacrosGoalLock();
        } else {
            this.previewGoalSelectionUnlock();
        }
        const saved = await this.saveCaloriesPreference();
        if (!saved) {
            this.setCaloriesPreference(previous);
            this.restoreGoalSelectionState(previousGoal, goalWasDisabled);
        }
    }

    async toggleExercisePreference(): Promise<void> {
        if (!this.hasExercisePreferenceControls() || this.savingExercisePreference) {
            return;
        }

        const previous = this.isExerciseEnabled();
        this.setExercisePreference(!previous);
        const saved = await this.saveExercisePreference();
        if (!saved) {
            this.setExercisePreference(previous);
        }
    }

    async saveGoalSelection(): Promise<void> {
        this.syncYouthGoalWarning();
        this.syncCaloriesToggleVisibility();

        if (this.savingGoalPreference) {
            return;
        }

        if (this.goalSelectTarget.disabled) {
            this.goalSelectTarget.value = this.lastSavedGoal;
            this.setSaveStatus('Show and unlock calories before changing your goal.', true);
            return;
        }

        const nextGoal = this.goalSelectTarget.value;
        if (nextGoal === this.lastSavedGoal) {
            return;
        }

        const previousGoal = this.lastSavedGoal;
        this.savingGoalPreference = true;

        try {
            const result = await this.persistGoalSelection(nextGoal);
            if (result?.success) {
                this.lastSavedGoal = nextGoal;
                this.applyProfileUpdate(result);
                this.setSaveStatus('Changes saved.');
                this.syncCaloriesToggleVisibility();
                return;
            }
        } finally {
            this.savingGoalPreference = false;
        }

        this.goalSelectTarget.value = previousGoal;
        this.syncYouthGoalWarning();
        this.syncCaloriesToggleVisibility();
        this.setSaveStatus('Could not save changes.', true);
    }

    syncCaloriesToggleVisibility(): void {
        this.caloriesToggleWrapperTarget.classList.remove('hidden');
        this.syncCalorieLockVisibility();
    }

    openLockModal(): void {
        if (!this.shouldShowCalorieLockControls()) {
            return;
        }
        this.lockModalTarget.classList.remove('hidden');
        this.lockConfirmStepTarget.classList.remove('hidden');
        this.lockPinStepTarget.classList.add('hidden');
        this.lockPinErrorTarget.textContent = '';
        this.clearPinInputs('lock');
    }

    closeLockModal(): void {
        this.lockModalTarget.classList.add('hidden');
    }

    showLockPinStep(): void {
        this.lockConfirmStepTarget.classList.add('hidden');
        this.lockPinStepTarget.classList.remove('hidden');
        this.lockPinErrorTarget.textContent = '';
        this.focusFirstPin('lock');
    }

    openUnlockModal(): void {
        if (!this.isCaloriesLocked()) {
            return;
        }
        this.showUnlockModal(null);
    }

    private showUnlockModal(pendingChange: boolean | null): void {
        if (!this.isCaloriesLocked()) {
            return;
        }
        this.pendingCaloriesPreferenceChange = pendingChange;
        this.unlockModalTarget.classList.remove('hidden');
        this.unlockPinErrorTarget.textContent = '';
        this.clearPinInputs('unlock');
        if (pendingChange === null) {
            this.unlockModalTitleTarget.textContent = 'Unlock calorie setting';
            this.unlockModalDescriptionTarget.textContent = 'Enter your 4-digit PIN to remove the lock from the calorie toggle.';
            this.unlockSubmitLabelTarget.textContent = 'Unlock';
        } else {
            this.unlockModalTitleTarget.textContent = pendingChange ? 'Unlock to show calories' : 'Unlock to hide calories';
            this.unlockModalDescriptionTarget.textContent = pendingChange
                ? 'Enter your 4-digit PIN to unlock the calorie toggle and show calories now.'
                : 'Enter your 4-digit PIN to unlock the calorie toggle and hide calories now.';
            this.unlockSubmitLabelTarget.textContent = pendingChange ? 'Show calories' : 'Hide calories';
        }
        this.focusFirstPin('unlock');
    }

    closeUnlockModal(): void {
        this.unlockModalTarget.classList.add('hidden');
        this.pendingCaloriesPreferenceChange = null;
        this.unlockModalTitleTarget.textContent = 'Unlock calorie setting';
        this.unlockModalDescriptionTarget.textContent = 'Enter your 4-digit PIN';
        this.unlockSubmitLabelTarget.textContent = 'Unlock';
    }

    handlePinInput(event: Event): void {
        const input = event.currentTarget as HTMLInputElement;
        const group = input.dataset.pinGroup;
        if (!group) {
            return;
        }

        if (input.value && !/^\d$/.test(input.value)) {
            input.value = '';
            return;
        }

        const inputs = this.getPinInputs(group);
        const index = inputs.indexOf(input);
        if (input.value && index < inputs.length - 1) {
            inputs[index + 1].focus();
        }
    }

    handlePinKeydown(event: KeyboardEvent): void {
        const input = event.currentTarget as HTMLInputElement;
        const group = input.dataset.pinGroup;
        if (!group || event.key !== 'Backspace') {
            return;
        }

        if (input.value) {
            input.value = '';
            return;
        }

        const inputs = this.getPinInputs(group);
        const index = inputs.indexOf(input);
        if (index > 0) {
            inputs[index - 1].focus();
        }
    }

    async submitLock(): Promise<void> {
        const pin = this.collectPin('lock');
        if (pin.length !== 4) {
            this.lockPinErrorTarget.textContent = 'Enter a valid 4-digit PIN.';
            return;
        }

        const previousGoal = this.goalSelectTarget.value;
        const goalWasDisabled = this.goalSelectTarget.disabled;
        this.setCaloriesPreference(false);
        this.previewTrackMacrosGoalLock();
        const result = await this.saveCalorieLockAction('lock', pin);
        if (!result?.success) {
            this.restoreGoalSelectionState(previousGoal, goalWasDisabled);
            this.lockPinErrorTarget.textContent = result.detail ?? 'Could not lock the calorie toggle.';
            return;
        }

        this.applyProfileUpdate(result);
        this.closeLockModal();
        this.setSaveStatus('Changes saved.');
    }

    async submitUnlock(): Promise<void> {
        const pin = this.collectPin('unlock');
        if (pin.length !== 4) {
            this.unlockPinErrorTarget.textContent = 'Enter your 4-digit PIN.';
            return;
        }

        if (this.pendingCaloriesPreferenceChange !== null) {
            const previous = this.isCaloriesEnabled();
            const previousGoal = this.goalSelectTarget.value;
            const goalWasDisabled = this.goalSelectTarget.disabled;
            this.setCaloriesPreference(this.pendingCaloriesPreferenceChange);
            if (!this.pendingCaloriesPreferenceChange) {
                this.previewTrackMacrosGoalLock();
            } else {
                this.previewGoalSelectionUnlock();
            }
            const result = await this.saveCaloriesPreference(pin);
            if (!result) {
                this.setCaloriesPreference(previous);
                this.restoreGoalSelectionState(previousGoal, goalWasDisabled);
                if (!this.unlockPinErrorTarget.textContent) {
                    this.unlockPinErrorTarget.textContent = 'Could not update the calorie preference.';
                }
            }
            return;
        }

        const previousGoal = this.goalSelectTarget.value;
        const goalWasDisabled = this.goalSelectTarget.disabled;
        this.previewGoalSelectionUnlock();
        const result = await this.saveCalorieLockAction('unlock', pin);
        if (!result?.success) {
            this.restoreGoalSelectionState(previousGoal, goalWasDisabled);
            this.unlockPinErrorTarget.textContent = result.detail ?? 'Could not unlock the calorie toggle.';
            return;
        }

        this.applyProfileUpdate(result);
        this.closeUnlockModal();
        this.setSaveStatus('Changes saved.');
    }

    /**
     * Set units to metric system
     */
    private setMetricUnits(convertValues = false): void {
        if (convertValues) {
            this.convertPhysicalInputs('metric');
        }
        this.unitToggleTarget.setAttribute('aria-checked', 'false');
        this.unitToggleTarget.classList.remove('bg-green-600');
        this.unitToggleTarget.classList.add('bg-zinc-300');
        this.toggleIndicatorTarget.classList.remove('translate-x-5');
        this.toggleIndicatorTarget.classList.add('translate-x-0');
        this.unitSystemInputTarget.value = 'metric';
        this.weightUnitTarget.textContent = 'kg';
        this.heightUnitTarget.textContent = 'cm';
        this.waterUnitTarget.textContent = 'ml';
    }

    /**
     * Set units to imperial system
     */
    private setImperialUnits(convertValues = false): void {
        if (convertValues) {
            this.convertPhysicalInputs('imperial');
        }
        this.unitToggleTarget.setAttribute('aria-checked', 'true');
        this.unitToggleTarget.classList.remove('bg-zinc-300');
        this.unitToggleTarget.classList.add('bg-green-600');
        this.toggleIndicatorTarget.classList.remove('translate-x-0');
        this.toggleIndicatorTarget.classList.add('translate-x-5');
        this.unitSystemInputTarget.value = 'imperial';
        this.weightUnitTarget.textContent = 'lb';
        this.heightUnitTarget.textContent = 'in';
        this.waterUnitTarget.textContent = 'fl oz';
    }

    /**
     * Sync the toggle UI with the user's saved unit preference
     */
    private syncToggleWithSavedValue(): void {
        const savedUnit = this.unitSystemInputTarget.value;

        if (savedUnit === 'imperial') {
            this.setImperialUnits();
        } else {
            this.setMetricUnits();
        }
    }

    private setCaloriesPreference(enabled: boolean): void {
        this.caloriesToggleTarget.setAttribute('aria-checked', enabled ? 'true' : 'false');
        if (enabled) {
            this.caloriesToggleTarget.classList.remove('bg-zinc-300');
            this.caloriesToggleTarget.classList.add('bg-green-600');
            this.caloriesToggleIndicatorTarget.classList.remove('translate-x-0');
            this.caloriesToggleIndicatorTarget.classList.add('translate-x-5');
        } else {
            this.caloriesToggleTarget.classList.remove('bg-green-600');
            this.caloriesToggleTarget.classList.add('bg-zinc-300');
            this.caloriesToggleIndicatorTarget.classList.remove('translate-x-5');
            this.caloriesToggleIndicatorTarget.classList.add('translate-x-0');
        }
        this.caloriesPreferenceInputTarget.value = enabled ? 'true' : 'false';
        this.syncCalorieLockVisibility();
    }

    private syncCaloriesToggleWithSavedValue(): void {
        const savedPreference = this.caloriesPreferenceInputTarget.value;
        const enabled = savedPreference === 'true' || savedPreference === 'True' || savedPreference === '1';
        this.setCaloriesPreference(enabled);
    }

    private isCaloriesEnabled(): boolean {
        const value = this.caloriesPreferenceInputTarget.value;
        return value === 'true' || value === 'True' || value === '1';
    }

    private setExercisePreference(enabled: boolean): void {
        if (!this.hasExercisePreferenceControls()) return;
        this.exerciseToggleTarget.setAttribute('aria-checked', enabled ? 'true' : 'false');
        if (enabled) {
            this.exerciseToggleTarget.classList.remove('bg-zinc-300');
            this.exerciseToggleTarget.classList.add('bg-green-600');
            this.exerciseToggleIndicatorTarget.classList.remove('translate-x-0');
            this.exerciseToggleIndicatorTarget.classList.add('translate-x-5');
        } else {
            this.exerciseToggleTarget.classList.remove('bg-green-600');
            this.exerciseToggleTarget.classList.add('bg-zinc-300');
            this.exerciseToggleIndicatorTarget.classList.remove('translate-x-5');
            this.exerciseToggleIndicatorTarget.classList.add('translate-x-0');
        }
        this.exercisePreferenceInputTarget.value = enabled ? 'true' : 'false';
    }

    private syncExerciseToggleWithSavedValue(): void {
        if (!this.hasExercisePreferenceControls()) return;
        const savedPreference = this.exercisePreferenceInputTarget.value;
        const enabled = savedPreference === 'true' || savedPreference === 'True' || savedPreference === '1';
        this.setExercisePreference(enabled);
    }

    private isExerciseEnabled(): boolean {
        if (!this.hasExercisePreferenceInputTarget) return false;
        const value = this.exercisePreferenceInputTarget.value;
        return value === 'true' || value === 'True' || value === '1';
    }

    private async saveExercisePreference(): Promise<boolean> {
        if (!this.hasExercisePreferenceInputTarget || this.savingExercisePreference) {
            return false;
        }

        this.savingExercisePreference = true;
        try {
            this.setSaveStatus('Saving changes…');
            const formData = new FormData();
            formData.set('show_exercise_pref', this.exercisePreferenceInputTarget.value);

            const response = await fetch('/user-profile/exercise-preference', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    ...csrfHeaders(),
                },
            });

            if (response.ok) {
                const payload = await response.json() as ProfileUpdateResponse;
                this.applyProfileUpdate(payload);
                this.setSaveStatus('Changes saved.');
                return true;
            }

            this.setSaveStatus(await this.readResponseDetail(response) ?? 'Could not save changes.', true);
            return false;
        } catch (error) {
            console.error('Failed to save exercise preference', error);
            this.setSaveStatus('Could not save changes.', true);
            return false;
        } finally {
            this.savingExercisePreference = false;
        }
    }

    private async saveCaloriesPreference(pin: string | null = null): Promise<boolean> {
        if (this.savingCaloriesPreference) {
            return false;
        }

        this.savingCaloriesPreference = true;
        try {
            this.setSaveStatus('Saving changes…');
            const formData = new FormData();
            formData.set('show_calories_pref', this.caloriesPreferenceInputTarget.value);
            if (pin) {
                formData.set('calorie_lock_pin', pin);
            }

            const response = await fetch('/user-profile/calorie-preference', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    ...csrfHeaders(),
                },
            });

            if (response.ok) {
                const payload = await response.json() as ProfileUpdateResponse;
                this.applyProfileUpdate(payload);
                this.closeUnlockModal();
                this.setSaveStatus('Changes saved.');
                return true;
            }

            if (response.status === 423) {
                this.showUnlockModal(this.isCaloriesEnabled());
                this.unlockPinErrorTarget.textContent = 'Enter your 4-digit PIN to change the calorie setting.';
                return false;
            }
            const detail = await this.readResponseDetail(response);
            if (detail && pin) {
                this.unlockPinErrorTarget.textContent = detail;
            }
            this.setSaveStatus(detail ?? 'Could not save changes.', true);
            return false;
        } catch (error) {
            console.error('Failed to save calorie preference', error);
            this.setSaveStatus('Could not save changes.', true);
            return false;
        } finally {
            this.savingCaloriesPreference = false;
        }
    }

    private syncCaloriesLockState(): void {
        if (!this.caloriesToggleWrapperTarget || !this.calorieLockEnabledInputTarget) {
            return;
        }
        const locked = this.isCaloriesLocked();
        if (locked) {
            this.caloriesToggleTarget.setAttribute('aria-disabled', 'true');
            this.caloriesToggleTarget.classList.add('opacity-60', 'cursor-not-allowed');
        } else {
            this.caloriesToggleTarget.removeAttribute('aria-disabled');
            this.caloriesToggleTarget.classList.remove('opacity-60', 'cursor-not-allowed');
        }
    }

    private syncCalorieLockVisibility(): void {
        if (!this.calorieLockControlsTarget) {
            return;
        }
        this.calorieLockControlsTarget.classList.remove('hidden');
    }

    private isCaloriesLocked(): boolean {
        return this.calorieLockEnabledInputTarget.value === 'true';
    }

    private isTrackMacrosSelected(): boolean {
        return this.goalSelectTarget.value === 'track_macros';
    }

    private shouldShowCalorieLockControls(): boolean {
        return true;
    }

    private previewTrackMacrosGoalLock(): void {
        this.goalSelectTarget.value = 'track_macros';
        this.goalSelectTarget.disabled = true;
        this.goalSelectTarget.classList.add('cursor-not-allowed', 'bg-zinc-100', 'text-zinc-500');
        this.syncYouthGoalWarning();
    }

    private previewGoalSelectionUnlock(): void {
        this.goalSelectTarget.disabled = false;
        this.goalSelectTarget.classList.remove('cursor-not-allowed', 'bg-zinc-100', 'text-zinc-500');
        this.syncYouthGoalWarning();
    }

    private restoreGoalSelectionState(goal: string, disabled: boolean): void {
        this.goalSelectTarget.value = goal;
        this.goalSelectTarget.disabled = disabled;
        this.goalSelectTarget.classList.toggle('cursor-not-allowed', disabled);
        this.goalSelectTarget.classList.toggle('bg-zinc-100', disabled);
        this.goalSelectTarget.classList.toggle('text-zinc-500', disabled);
        this.syncYouthGoalWarning();
    }

    private clearPendingCalorieLockSubmission(): void {
        this.calorieLockActionInputTarget.value = '';
        this.calorieLockPinInputTarget.value = '';
        this.lockPinErrorTarget.textContent = '';
        this.unlockPinErrorTarget.textContent = '';
        this.pendingCaloriesPreferenceChange = null;
        this.clearPinInputs('lock');
        this.clearPinInputs('unlock');
    }

    private getPinInputs(group: string): HTMLInputElement[] {
        const inputs = Array.from(
            this.element.querySelectorAll<HTMLInputElement>(`input[data-pin-group="${group}"]`)
        );
        inputs.sort((a, b) => Number(a.dataset.pinIndex) - Number(b.dataset.pinIndex));
        return inputs;
    }

    private collectPin(group: string): string {
        return this.getPinInputs(group).map((input) => input.value).join('');
    }

    private clearPinInputs(group: string): void {
        this.getPinInputs(group).forEach((input) => {
            input.value = '';
        });
    }

    private focusFirstPin(group: string): void {
        const inputs = this.getPinInputs(group);
        if (inputs.length > 0) {
            inputs[0].focus();
        }
    }

    private convertPhysicalInputs(targetSystem: 'metric' | 'imperial'): void {
        const weightInput = this.profileFormTarget.querySelector<HTMLInputElement>('input[name="weight"]');
        const heightInput = this.profileFormTarget.querySelector<HTMLInputElement>('input[name="height"]');
        const waterGoalInput = this.profileFormTarget.querySelector<HTMLInputElement>('input[name="water_goal"]');

        if (weightInput) {
            const convertedWeight = this.convertNumericInputValue(
                weightInput.value,
                targetSystem === 'imperial' ? 1 / 0.453592 : 0.453592
            );
            if (convertedWeight !== null) {
                weightInput.value = convertedWeight;
            }
        }

        if (heightInput) {
            const convertedHeight = this.convertNumericInputValue(
                heightInput.value,
                targetSystem === 'imperial' ? 1 / 2.54 : 2.54
            );
            if (convertedHeight !== null) {
                heightInput.value = convertedHeight;
            }
        }

        if (waterGoalInput) {
            const convertedWaterGoal = this.convertNumericInputValue(
                waterGoalInput.value,
                targetSystem === 'imperial' ? 1 / 29.5735295625 : 29.5735295625
            );
            if (convertedWaterGoal !== null) {
                waterGoalInput.value = convertedWaterGoal;
            }
        }
    }

    private convertNumericInputValue(rawValue: string, multiplier: number): string | null {
        const value = Number.parseFloat(rawValue);
        if (!Number.isFinite(value) || value <= 0) {
            return null;
        }
        const precisionFactor = 10 ** UserProfileController.conversionDisplayPrecision;
        const converted = Math.round((value * multiplier) * precisionFactor) / precisionFactor;
        return converted.toFixed(UserProfileController.conversionDisplayPrecision).replace(/\.?0+$/, '');
    }

    private async saveUnitSystemPreference(unitSystem: 'metric' | 'imperial'): Promise<ProfileUpdateResponse | null> {
        try {
            const formData = new FormData();
            formData.set('unit_system', unitSystem);

            const response = await fetch('/user-profile/unit-system', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    ...csrfHeaders(),
                },
            });

            if (!response.ok) {
                return { success: false, detail: await this.readResponseDetail(response) };
            }
            return await response.json() as ProfileUpdateResponse;
        } catch (error) {
            console.error('Failed to save unit system', error);
            return null;
        }
    }

    private async persistGoalSelection(goal: string): Promise<ProfileUpdateResponse | null> {
        try {
            this.setSaveStatus('Saving changes…');
            const formData = new FormData();
            formData.set('goal', goal);

            const response = await fetch('/user-profile/goal', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    ...csrfHeaders(),
                },
            });

            if (!response.ok) {
                return { success: false, detail: await this.readResponseDetail(response) };
            }

            return await response.json() as ProfileUpdateResponse;
        } catch (error) {
            console.error('Failed to save goal preference', error);
            return null;
        }
    }

    private async saveCalorieLockAction(
        action: 'lock' | 'unlock',
        pin: string,
    ): Promise<ProfileUpdateResponse> {
        try {
            this.setSaveStatus('Saving changes…');
            const formData = new FormData();
            formData.set('action', action);
            formData.set('calorie_lock_pin', pin);

            const response = await fetch('/user-profile/calorie-lock', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    ...csrfHeaders(),
                },
            });

            if (response.ok) {
                return await response.json() as ProfileUpdateResponse;
            }

            return { success: false, detail: await this.readResponseDetail(response) };
        } catch (error) {
            console.error('Failed to update calorie lock', error);
            return { success: false, detail: 'Please try again.' };
        }
    }

    private async persistProfileFields(): Promise<ProfileUpdateResponse | null> {
        try {
            const response = await fetch('/user-profile/profile-fields', {
                method: 'POST',
                body: new FormData(this.profileFormTarget),
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    ...csrfHeaders(),
                },
            });

            if (!response.ok) {
                return { success: false, detail: await this.readResponseDetail(response) };
            }

            return await response.json() as ProfileUpdateResponse;
        } catch (error) {
            console.error('Failed to autosave profile', error);
            return null;
        }
    }

    private applyProfileUpdate(payload: ProfileUpdateResponse): void {
        if (payload.goal_settings_html !== undefined) {
            this.goalSettingsPanelTarget.innerHTML = payload.goal_settings_html;
        }
        if (payload.profile_summary_html !== undefined) {
            this.profileSummaryPanelTarget.innerHTML = payload.profile_summary_html;
        }
        if (payload.targets_panel_html !== undefined) {
            this.targetsPanelTarget.innerHTML = payload.targets_panel_html;
        }
        if (payload.goal) {
            this.goalSelectTarget.value = payload.goal;
            this.lastSavedGoal = payload.goal;
        }
        if (payload.unit_system) {
            this.unitSystemInputTarget.value = payload.unit_system;
        }
        if (typeof payload.show_calories_pref === 'boolean') {
            this.caloriesPreferenceInputTarget.value = payload.show_calories_pref ? 'true' : 'false';
            this.syncCaloriesToggleWithSavedValue();
        }
        if (typeof payload.show_exercise_pref === 'boolean' && this.hasExercisePreferenceControls()) {
            this.exercisePreferenceInputTarget.value = payload.show_exercise_pref ? 'true' : 'false';
            this.syncExerciseToggleWithSavedValue();
        }
        if (typeof payload.calorie_lock_enabled === 'boolean') {
            this.calorieLockEnabledInputTarget.value = payload.calorie_lock_enabled ? 'true' : 'false';
            this.syncCaloriesLockState();
        }
        this.syncCaloriesToggleVisibility();
        this.syncCalorieLockVisibility();
        this.syncYouthGoalWarning();
    }

    private hasExercisePreferenceControls(): boolean {
        return this.hasExerciseToggleTarget
            && this.hasExerciseToggleIndicatorTarget
            && this.hasExercisePreferenceInputTarget;
    }

    private setSaveStatus(message: string, isError = false): void {
        this.saveStatusTarget.textContent = message;
        this.saveStatusTarget.classList.toggle('text-red-600', isError);
        this.saveStatusTarget.classList.toggle('text-zinc-500', !isError);
    }

    private async readResponseDetail(response: Response): Promise<string | undefined> {
        try {
            const payload = await response.json() as { detail?: string };
            return payload.detail;
        } catch {
            return undefined;
        }
    }
}
